import { SubscribePlan } from '../../../../types';
import { OnetimePeriod, PlanTemplateType, SubscriptionPeriod } from '../types';

/**
 * 쿼리 매개변수와 결제 정보에 따라 플랜 타입을 결정합니다.
 *
 * @param {string} queryPlanType - 쿼리 문자열로 전달된 플랜 타입 (예: 'basic', 'pro').
 * @param {string} [planPaymentInfoPlanType] - 결제 정보에서 추출된 플랜 타입.
 * @returns {SubscribePlan} - 결정된 플랜 타입을 반환합니다. 전달된 queryPlanType이 유효하지 않은 경우
 *                            planPaymentInfoPlanType을 사용하며, 둘 다 유효하지 않으면 기본값으로 'pro'를 반환합니다.
 */
export const determinePlanType = (
  queryPlanType: string,
  planPaymentInfoPlanType: string | undefined,
): SubscribePlan => {
  // queryPlanType이 'basic'이나 'pro'인 경우 해당 값을 반환
  if (queryPlanType === 'basic' || queryPlanType === 'pro') {
    return queryPlanType as SubscribePlan;
  }

  // queryPlanType이 유효하지 않은 경우 planPaymentInfoPlanType을 사용
  if (
    planPaymentInfoPlanType === 'basic' ||
    planPaymentInfoPlanType === 'pro'
  ) {
    return planPaymentInfoPlanType as SubscribePlan;
  }

  // 둘 다 유효하지 않은 경우 기본값으로 'pro'를 반환
  return 'pro';
};

/**
 * 다양한 입력값을 기반으로 최대 동시 사용자 수 (maxCcu) 값을 계산합니다.
 *
 * @param {SubscribePlan} planType - 결정된 플랜 타입 ('basic' 또는 'pro').
 * @param {number} [planPaymentInfoMaxCcu] - 결제 정보에서 제공된 maxCcu 값.
 * @returns {number} - 계산된 maxCcu 값을 반환합니다. planPaymentInfoMaxCcu가 존재하지 않으면, planType에 따라 기본값을 반환합니다.
 */
export const determineMaxCcu = (
  planType: SubscribePlan,
  planPaymentInfoMaxCcu?: number,
): number => {
  // planPaymentInfoMaxCcu가 존재하고 유효한 경우 해당 값을 반환
  if (planPaymentInfoMaxCcu !== undefined && !isNaN(planPaymentInfoMaxCcu)) {
    return planPaymentInfoMaxCcu;
  }

  // planType이 'pro'면 10을 반환, 'basic'이면 20을 반환
  if (planType === 'pro') {
    return 10;
  } else if (planType === 'basic') {
    return 20;
  }

  // 기본값이 없을 경우에도 기본값으로 10을 반환 (예: planType이 'basic' 또는 'pro'가 아닌 경우)
  return 10;
};

/**
 * 쿼리 매개변수와 플랜 템플릿 타입에 따라 템플릿 타입을 결정합니다.
 *
 * @param {string} queryPlanTemplateType - 쿼리 문자열로 전달된 플랜 템플릿 타입 (예: 1, 3).
 * @param {PlanTemplateType} [planTemplateType] - 결제 정보에서 추출된 플랜 템플릿 타입.
 * @returns {PlanTemplateType} - 결정된 플랜 템플릿 타입을 반환합니다. queryPlanTemplateType이 1이나 3이 아니면 planTemplateType을 사용하며,
 *                     둘 다 유효하지 않으면 기본값으로 1을 반환합니다.
 */
export const determinePlanTemplateType = (
  queryPlanTemplateType: string,
  planTemplateType?: PlanTemplateType,
): PlanTemplateType => {
  // queryPlanTemplateType이 1이나 3인 경우 해당 값을 반환
  if (
    queryPlanTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION ||
    queryPlanTemplateType === PlanTemplateType.PRODUCT_ONETIME
  ) {
    return queryPlanTemplateType as PlanTemplateType;
  }

  // queryPlanTemplateType이 유효하지 않은 경우 planTemplateType을 사용
  if (planTemplateType !== undefined && planTemplateType != null) {
    return planTemplateType;
  }

  // 둘 다 유효하지 않은 경우 기본값으로 1을 반환
  return PlanTemplateType.PRODUCT_SUBSCRIPTION;
};

/**
 * 쿼리 매개변수와 플랜 기간에 따라 기간 타입을 결정합니다.
 *
 * @param {string} queryOnetimePeriod - 쿼리 문자열로 전달된 기간 (예: '1D', '3D', '1M', '2M', ..., '12M').
 * @param {string} [planOnetimePeriod] - 결제 정보에서 제공된 기간.
 * @returns {string} - 결정된 기간 타입을 반환합니다. queryOnetimePeriod가 유효하지 않으면 planOnetimePeriod를 사용하며,
 *                     둘 다 유효하지 않으면 기본값으로 '1M'을 반환합니다.
 */
export const determineOnetimePeriod = (
  queryOnetimePeriod: string,
  planOnetimePeriod?: string,
): OnetimePeriod => {
  const validPeriods = [
    '1D',
    '3D',
    '1M',
    '2M',
    '3M',
    '4M',
    '5M',
    '6M',
    '7M',
    '8M',
    '9M',
    '10M',
    '11M',
    '12M',
  ];

  // queryOnetimePeriod가 유효한 기간인 경우 해당 값을 반환
  if (validPeriods.includes(queryOnetimePeriod)) {
    return queryOnetimePeriod as OnetimePeriod;
  }

  // queryOnetimePeriod가 유효하지 않은 경우 planOnetimePeriod를 사용
  if (
    planOnetimePeriod !== undefined &&
    validPeriods.includes(planOnetimePeriod)
  ) {
    return planOnetimePeriod as OnetimePeriod;
  }

  // 둘 다 유효하지 않은 경우 기본값으로 '1M'을 반환
  return '1M';
};

/**
 * 쿼리 매개변수와 플랜 구독 기간에 따라 구독 기간을 결정합니다.
 *
 * @param {number} querySubscriptionPeriod - 쿼리 문자열로 전달된 구독 기간 (예: 30, 365).
 * @param {number} [planSubscriptionPeriod] - 결제 정보에서 제공된 구독 기간.
 * @returns {string} - 결정된 구독 기간을 반환합니다. querySubscriptionPeriod가 유효하지 않으면 planSubscriptionPeriod를 사용하며,
 *                     둘 다 유효하지 않으면 기본값으로 30을 반환합니다.
 */
export const determineSubscriptionPeriod = (
  querySubscriptionPeriod: string,
  planSubscriptionPeriod?: number,
): SubscriptionPeriod => {
  const validPeriods = ['30', '365'];

  if (validPeriods.includes(querySubscriptionPeriod)) {
    return querySubscriptionPeriod as SubscriptionPeriod;
  }

  if (
    planSubscriptionPeriod !== undefined &&
    validPeriods.includes(String(planSubscriptionPeriod))
  ) {
    return String(planSubscriptionPeriod) as SubscriptionPeriod;
  }

  // 둘 다 유효하지 않은 경우 기본값으로 30을 반환
  return '30';
};
